import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { http } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ShareButton from "@/components/ShareButton";
import BackToTopButton from "@/components/BackToTopButton";
import { useAlertCount } from "@/components/AlertsPanel";
import { useSEO } from "@/hooks/useSEO";
import { scrollToPageTop } from "@/lib/scroll";
import { BookOpen, ArrowRight, Loader2, Tag } from "lucide-react";

/** Public blog index — lists every published post ordered by newest first. */
export default function BlogIndex() {
  const alertCount = useAlertCount();
  React.useEffect(() => { scrollToPageTop(); }, []);
  useSEO({
    title: "Popoyo.app Blog — Popoyo Travel & Surf Guides",
    description:
      "Local guides, surf reports, and travel tips for Popoyo, Nicaragua. Written by locals, updated regularly.",
    path: "/blog",
  });

  const q = useQuery({
    queryKey: ["blog", "list"],
    queryFn: () => http.get("/blog").then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Header alertCount={alertCount} />
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex items-center gap-3 flex-wrap">
          <BookOpen className="w-6 h-6 text-[color:var(--coral)]" />
          <div className="flex-1">
            <p className="eyebrow text-[color:var(--coral)]">Local guides & stories</p>
            <h1 className="text-4xl sm:text-5xl font-bold" style={{ fontFamily: "Outfit" }}>
              Popoyo Blog
            </h1>
          </div>
          <ShareButton
            path="/blog"
            title="Popoyo.app Blog — Popoyo Travel & Surf Guides"
            text="Local guides, surf reports, and travel tips for Popoyo, Nicaragua."
            testId="share-blog-btn"
          />
        </div>

        {q.isLoading ? (
          <div className="flex items-center gap-2 text-[color:var(--ink-2)]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (q.data?.length ?? 0) === 0 ? (
          <p className="text-[color:var(--ink-2)] italic">
            No articles yet — check back soon for local surf guides, food picks, and travel tips.
          </p>
        ) : (
          <ul className="space-y-3" data-testid="blog-list">
            {q.data.map((p) => (
              <li key={p.id}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-item-${p.slug}`}
                  className="clay p-5 sm:p-6 block hover:scale-[1.005] transition"
                >
                  <p className="eyebrow text-[color:var(--coral)]">{p.category || "guide"}</p>
                  <h2 className="text-xl sm:text-2xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>
                    {p.title}
                  </h2>
                  {p.excerpt && (
                    <p className="text-sm text-[color:var(--ink-2)] mt-2">{p.excerpt}</p>
                  )}
                  <p className="text-[11px] text-[color:var(--ink-2)] mt-3 flex items-center gap-2 flex-wrap">
                    {p.published_at && <span>{new Date(p.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span>}
                    {p.tags?.length > 0 && (
                      <>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1 flex-wrap">
                          <Tag className="w-3 h-3" />
                          {p.tags.slice(0, 3).join(" · ")}
                        </span>
                      </>
                    )}
                    <span className="ml-auto inline-flex items-center gap-1 text-[color:var(--coral)] font-semibold">
                      Read <ArrowRight className="w-3 h-3" />
                    </span>
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
      <BackToTopButton />
      <Footer />
    </div>
  );
}
